// 웹뷰 브리지 인터페이스 정의
interface WebViewBridge {
    showNotificationBanner: (title: string, message: string, type: NotificationType) => void;
    log: (message: string) => void;
    saveAuthToken: (token: string) => void;
    getAuthToken: () => string;
}

export type NotificationType = 'emergency' | 'bio' | 'falldetection' | 'safezone';

// 안드로이드에서 주입한 ReactNotificationHelper 타입 정의
interface ReactNotificationHelper {
    sendNotification: (title: string, message: string, type: NotificationType) => boolean;
    isAvailable: () => boolean;
}

declare global {
    interface Window {
        Android?: WebViewBridge;
        webkit?: {
            messageHandlers?: {
                notification?: {
                    postMessage: (data: any) => void;
                };
                iOS?: {
                    postMessage: (data: any) => void;
                };
            };
        };
        ReactNotificationHelper?: ReactNotificationHelper;
        sendEmergencyNotification?: (title: string, message: string) => boolean;
    }
}

// Android 객체 대기 함수
function waitForAndroid(): Promise<boolean> {
    return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 50;
        
        function checkAndroid() {
            attempts++;
            
            if (window.Android && typeof window.Android.showNotificationBanner === 'function') {
                resolve(true);
                return;
            }
            
            if (attempts >= maxAttempts) {
                resolve(false);
                return;
            }
            
            setTimeout(checkAndroid, 100);
        }
        
        checkAndroid();
    });
}

export async function showNotificationBanner(title: string, message: string, type: NotificationType): Promise<void> {
    // console.log(`WebView Bridge - showNotificationBanner called: ${title} - ${message} (${type})`);
    
    // 1차: iOS WebKit 확인
    if (window.webkit?.messageHandlers?.notification) {
        // console.log('iOS WebKit notification handler found, sending notification');
        try {
            window.webkit.messageHandlers.notification.postMessage({
                title, 
                message, 
                type,
                timestamp: Date.now()
            });
            return;
        } catch (error) {
            console.error('iOS notification error:', error);
        }
    }

    // 2차: 기본 Android 객체 확인
    if (window.Android && typeof window.Android.showNotificationBanner === 'function') {
        // console.log('Android bridge found, sending notification');
        window.Android.showNotificationBanner(title, message, type);
        
        if (window.Android.log) {
            window.Android.log(`WebView notification sent: ${title} - ${message} (${type})`);
        }
        return;
    }

    // 3차: ReactNotificationHelper 확인
    if (window.ReactNotificationHelper && window.ReactNotificationHelper.isAvailable()) {
        // console.log('ReactNotificationHelper found, sending notification');
        const result = window.ReactNotificationHelper.sendNotification(title, message, type);
        if (result) {
            return;
        }
    }

    // 4차: 전역 함수 확인
    if (window.sendEmergencyNotification && typeof window.sendEmergencyNotification === 'function') {
        // console.log('sendEmergencyNotification function found, sending notification');
        const result = window.sendEmergencyNotification(title, message);
        if (result) {
            return;
        }
    }

    // 5차: Android 객체 대기 후 재시도
    // console.log('Waiting for Android bridge...');
    const androidAvailable = await waitForAndroid();
    
    if (androidAvailable && window.Android) {
        // console.log('Android bridge available after waiting, sending notification');
        window.Android.showNotificationBanner(title, message, type);
        
        if (window.Android.log) {
            window.Android.log(`WebView notification sent (delayed): ${title} - ${message} (${type})`);
        }
        return;
    }

    // 6차: 커스텀 이벤트 발송
    try {
        // console.log('Sending custom notification event');
        const notificationEvent = new CustomEvent('sendNotification', {
            detail: { title, message, type }
        });
        window.dispatchEvent(notificationEvent);
    } catch (e) {
        console.warn('Custom event dispatch failed:', e);
    }
    
    // console.warn('No native bridge available for notifications');
}

export const webviewBridge = {
    // 네이티브 앱 여부 확인
    isNativeApp: (): boolean => {
        const isAndroid = typeof window.Android === 'object' && window.Android !== null;
        const isIOS = typeof window.webkit?.messageHandlers?.notification === 'object' ||
                     typeof window.webkit?.messageHandlers?.iOS === 'object';
        return isAndroid || isIOS;
    },

    // 로그 출력 (네이티브만)
    log: (message: string): void => {
        if (window.Android?.log) {
            window.Android.log(message);
        } else if (window.webkit?.messageHandlers?.iOS) {
            // iOS 로그 전송
            try {
                window.webkit.messageHandlers.iOS.postMessage({
                    action: 'log',
                    message: message,
                    timestamp: Date.now()
                });
            } catch (e) {
                // console.log('iOS log failed:', e);
            }
        }
        // console.log(`WebView Bridge Log: ${message}`);
    },

    // 알림 전송
    sendNotification: (title: string, message: string, type: NotificationType = 'emergency'): Promise<void> => {
        return showNotificationBanner(title, message, type);
    },

    // Android 객체 대기 함수
    waitForAndroid: (timeout: number = 5000): Promise<boolean> => {
        return new Promise((resolve) => {
            const startTime = Date.now();
            
            const checkAndroid = () => {
                if (typeof window.Android === 'object' && window.Android !== null) {
                    resolve(true);
                    return;
                }
                
                if (Date.now() - startTime > timeout) {
                    resolve(false);
                    return;
                }
                
                setTimeout(checkAndroid, 100);
            };
            
            checkAndroid();
        });
    },

    // 브리지 상태 확인 함수
    checkBridgeStatus: () => {
        const status = {
            timestamp: new Date().toISOString(),
            android: {
                exists: !!window.Android,
                hasShowNotificationBanner: !!(window.Android && window.Android.showNotificationBanner),
                hasLog: !!(window.Android && window.Android.log),
                hasSaveAuthToken: !!(window.Android && window.Android.saveAuthToken),
                hasGetAuthToken: !!(window.Android && window.Android.getAuthToken)
            },
            ios: {
                exists: !!window.webkit,
                hasMessageHandlers: !!(window.webkit && window.webkit.messageHandlers),
                hasNotificationBanner: !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.notification),
                hasIOSHandler: !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.iOS)
            },
            userAgent: navigator.userAgent,
            url: window.location.href,
            isNativeApp: webviewBridge.isNativeApp()
        };
        
        // console.log('WebView Bridge Status:', status);
        
        if (window.Android?.log) {
            window.Android.log(`Bridge Status: ${JSON.stringify(status)}`);
        } else if (window.webkit?.messageHandlers?.iOS) {
            try {
                window.webkit.messageHandlers.iOS.postMessage({
                    action: 'bridgeStatus',
                    status: status
                });
            } catch (e) {
                console.error('iOS bridge status send failed:', e);
            }
        }
        
        return status;
    },

    // 인증 토큰을 네이티브 앱에 저장
    saveAuthToken: (token: string): Promise<void> => {
        return new Promise((resolve) => {
            try {
                if (!webviewBridge.isNativeApp()) {
                    resolve();
                    return;
                }

                // Android 처리
                if (window.Android?.saveAuthToken) {
                    window.Android.saveAuthToken(token);
                    resolve();
                    return;
                }

                // iOS 처리
                if (window.webkit?.messageHandlers?.iOS) {
                    try {
                        window.webkit.messageHandlers.iOS.postMessage({
                            action: 'saveAuthToken',
                            token: token,
                            timestamp: Date.now()
                        });
                        resolve();
                        return;
                    } catch (error) {
                        console.error('iOS saveAuthToken error:', error);
                    }
                }

                // 네이티브 브리지를 찾을 수 없는 경우
                // console.warn('Native bridge not found for saveAuthToken');
                resolve(); // reject 대신 resolve로 변경 (에러로 인한 로그아웃 방지)
            } catch (error) {
                console.error('saveAuthToken error:', error);
                resolve(); // reject 대신 resolve로 변경
            }
        });
    },

    // 저장된 인증 토큰 조회
    getAuthToken: (): Promise<string> => {
        return new Promise((resolve) => {
            try {
                // console.log('🔍 [WebViewBridge] getAuthToken called')
                // console.log('🔍 [WebViewBridge] isNativeApp:', webviewBridge.isNativeApp())
                // console.log('🔍 [WebViewBridge] window.Android exists:', !!window.Android)
                // console.log('🔍 [WebViewBridge] window.Android.getAuthToken exists:', !!(window.Android?.getAuthToken))
                
                if (!webviewBridge.isNativeApp()) {
                    // console.log('❌ [WebViewBridge] Not a native app')
                    resolve('');
                    return;
                }

                // Android 처리
                if (window.Android?.getAuthToken) {
                    // console.log('✅ [WebViewBridge] Calling Android.getAuthToken()')
                    const token = window.Android.getAuthToken();
                    // console.log('🔍 [WebViewBridge] Android returned token:', token ? `${token.substring(0, 20)}...` : 'NONE')
                    resolve(token || '');
                    return;
                }

                // iOS 처리 - iOS는 비동기 응답을 받아야 하므로 이벤트 리스너 사용
                if (window.webkit?.messageHandlers?.iOS) {
                    // console.log('✅ [WebViewBridge] iOS handler found, requesting token...')
                    const timeoutId = setTimeout(() => {
                        // console.log('⏱️ [WebViewBridge] iOS getAuthToken timeout')
                        resolve(''); // 타임아웃 시 빈 문자열 반환
                    }, 1000);

                    // iOS로부터 토큰을 받기 위한 이벤트 리스너
                    const handleAuthToken = (event: any) => {
                        clearTimeout(timeoutId);
                        window.removeEventListener('authTokenResponse', handleAuthToken);
                        const token = event.detail?.token || '';
                        // console.log('🔍 [WebViewBridge] iOS returned token:', token ? `${token.substring(0, 20)}...` : 'NONE')
                        resolve(token);
                    };

                    window.addEventListener('authTokenResponse', handleAuthToken);

                    try {
                        window.webkit.messageHandlers.iOS.postMessage({
                            action: 'getAuthToken',
                            timestamp: Date.now()
                        });
                    } catch (error) {
                        clearTimeout(timeoutId);
                        window.removeEventListener('authTokenResponse', handleAuthToken);
                        console.error('❌ [WebViewBridge] iOS getAuthToken error:', error);
                        resolve('');
                    }
                    return;
                }

                // 네이티브 브리지를 찾을 수 없는 경우
                console.warn('❌ [WebViewBridge] Native bridge not found for getAuthToken');
                resolve(''); // reject 대신 resolve로 변경
            } catch (error) {
                console.error('❌ [WebViewBridge] getAuthToken error:', error);
                resolve(''); // reject 대신 resolve로 변경
            }
        });
    }
};

// 편의를 위한 개별 export 함수들
export const saveAuthToken = webviewBridge.saveAuthToken;
export const getAuthToken = webviewBridge.getAuthToken;

// 마지막으로 동기화된 토큰
let lastSyncedToken = '';

// 쿠키에서 토큰을 읽어와서 네이티브로 전달하는 함수 (Android/iOS 공용)
const syncTokenToNative = async () => {
    try {
        const token = document.cookie
            .split(';')
            .find(row => row.trim().startsWith('access_token='))
            ?.split('=')[1];

        // 토큰이 있고, 네이티브 앱이며, 마지막으로 동기화한 토큰과 다를 경우에만 동기화
        // Android: window.Android.saveAuthToken 호출 (기존 로직과 동일, 변경 시에만 호출되어 부하 감소)
        // iOS: messageHandlers.iOS.postMessage 호출
        if (token && webviewBridge.isNativeApp() && token !== lastSyncedToken) {
            // console.log('🔄 [WebViewBridge] Syncing token to native app...');
            await webviewBridge.saveAuthToken(token);
            lastSyncedToken = token;
        }
    } catch (error) {
        // 무시
    }
};

// 페이지 로드 완료 시 및 주기적으로 토큰 동기화
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(async () => {
                await syncTokenToNative();
            }, 1000);
        });
    } else {
        setTimeout(async () => {
            await syncTokenToNative();
        }, 1000);
    }
    
    // 주기적으로 토큰 확인 및 동기화 (5초마다)
    // 백엔드에서 쿠키를 갱신했을 때 네이티브와 동기화하기 위함
    // Android/iOS 모두 1시간 갱신 정책에 대응하기 위해 필요함
    setInterval(async () => {
        await syncTokenToNative();
    }, 5000);
}

if (webviewBridge.isNativeApp()) {
    // 초기화 시 브리지 상태 체크
    webviewBridge.checkBridgeStatus();
}

// 전역에서 접근 가능하도록 설정
(window as any).webviewBridge = webviewBridge; 
 